import {
  format,
  getTime,
  formatDistanceToNow,
  formatDistanceToNowStrict,
  isDate,
  isValid,
} from 'date-fns';
import i18n from 'src/i18n';

// ----------------------------------------------------------------------
const TIME_WITH_TIMEZONE_FORMAT = 'HH:mm:ss.SSS';

/**
 * Parses a UTC date string preserving the day-of-week in local timezone
 * Useful when backend returns dates like "2025-10-20T00:00:00Z" representing
 * Monday at midnight in the user's timezone (already converted by backend).
 * Standard new Date() conversion would shift the day in negative UTC offsets.
 *
 * Example: "2025-10-20T00:00:00Z" (Monday UTC) in Venezuela (UTC-4)
 * - new Date(): Sunday 20:00 ❌
 * - parseUTCDateAsLocal(): Monday 00:00 ✅
 *
 * @param {string} utcDateString - ISO 8601 UTC date string (e.g., "2025-10-20T00:00:00Z")
 * @returns {Date|null} Date object with preserved day-of-week at midnight local time
 */
export const parseUTCDateAsLocal = (utcDateString) => {
  if (!utcDateString) return null;

  try {
    const date = new Date(utcDateString);
    if (isNaN(date.getTime())) return null;

    // Extract UTC date components and reconstruct as local date
    const year = date.getUTCFullYear();
    const month = date.getUTCMonth();
    const day = date.getUTCDate();

    return new Date(year, month, day, 0, 0, 0, 0);
  } catch (error) {
    console.warn('Error parsing UTC date as local:', error);
    return null;
  }
};

/**
 * Formats a Date object or date string to YYYY-MM-DD format for API submission.
 * Use this when sending dates to the backend for Date scalar fields.
 *
 * @param {Date|string|null} date - Date object or string to format
 * @returns {string|null} Date in YYYY-MM-DD format or null if invalid
 */
export const toDateString = (date) => {
  if (!date) return null;

  try {
    let dateObj = date;
    if (typeof date === 'string') {
      dateObj = new Date(date);
    }
    if (!isDate(dateObj) || !isValid(dateObj)) return null;

    return format(dateObj, 'yyyy-MM-dd');
  } catch {
    return null;
  }
};

export function fDate(date) {
  if (!date) return '-';

  try {
    // Check if this is a date-only string (YYYY-MM-DD format without time)
    // These should not be timezone-converted since billing dates are not timezoned
    if (typeof date === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(date)) {
      const parsed = parseUTCDateAsLocal(date + 'T00:00:00Z');
      if (parsed) {
        return format(parsed, 'dd MMMM yyyy');
      }
    }

    // Check if this is an ISO DateTime string at midnight UTC (common for billing dates)
    // e.g., "2025-11-01T00:00:00.000Z" or "2025-11-01T00:00:00Z"
    if (typeof date === 'string' && /^\d{4}-\d{2}-\d{2}T00:00:00/.test(date)) {
      const parsed = parseUTCDateAsLocal(date);
      if (parsed) {
        return format(parsed, 'dd MMMM yyyy');
      }
    }

    return format(new Date(date), 'dd MMMM yyyy');
  } catch {
    return '-';
  }
}

export function fDateTime(date) {
  return format(new Date(date), 'dd MMM yyyy HH:mm');
}

export function fTimestamp(date) {
  return getTime(new Date(date));
}

export function fDateTimeSuffix(date) {
  return format(new Date(date), 'dd/MM/yyyy hh:mm p');
}

export function fToNow(date, options) {
  if (!date) return;
  return formatDistanceToNow(new Date(date), {
    addSuffix: true,
    ...options,
  });
}

// Same as fToNow but without the "about"/"alrededor de" fuzzing.
export function fToNowStrict(date, options) {
  if (!date) return;
  return formatDistanceToNowStrict(new Date(date), {
    addSuffix: true,
    ...options,
  });
}

/**
 * Formats a Date object to HH:mm:ss.SSS time with timezone string
 * @param {Date} date - Date object to format
 * @returns {string} Formatted time string or empty string if invalid
 */
export const formatTimeWithTimezone = (date) => {
  try {
    if (!isValid(date) || !isDate(date)) return '';
    return format(date, TIME_WITH_TIMEZONE_FORMAT);
  } catch (error) {
    return '';
  }
};

const SECONDS_PER_DAY = 86400;
const HOURS_PER_DAY = 24;

/**
 * Convert seconds to HH:MM:SS
 * If seconds exceeds 24 hours, hours will be greater than 24 (30:05:10)
 *
 * @param {number} seconds
 * @returns {string}
 */

export const secondsToHms = (seconds) => {
  const days = Math.floor(seconds / SECONDS_PER_DAY);
  const remainderSeconds = seconds % SECONDS_PER_DAY;
  const hms = new Date(remainderSeconds * 1000).toISOString().substring(14, 19);
  return hms.replace(/^(\d+)/, (h) =>
    `${Number(h) + days * HOURS_PER_DAY}`.padStart(2, '0')
  );
};

/**
 * Checks if the user's locale uses 12-hour time format
 * @param {string} [locale=navigator.language] - Locale to check (defaults to user's browser language)
 * @returns {boolean} True if locale uses 12-hour format (AM/PM), false if 24-hour format
 */
export const is12HourFormat = () => {
  const detectedLocale = getLocale();
  const format = new Intl.DateTimeFormat(detectedLocale, {
    hour: '2-digit',
    hour12: true,
  });
  const testDate = new Date(2023, 0, 1, 13); // 1 PM
  return format.format(testDate).includes('PM');
};

/**
 * Converts UTC date from backend to user's local timezone and locale format
 * @param {string} utcDateString - ISO 8601 UTC date string from backend
 * @returns {string} Date formatted according to user's locale and timezone
 */
export function formatDateToLocale(
  utcDateString,
  options = {
    includeTime: true,
  }
) {
  if (!utcDateString) return '';

  try {
    const date = new Date(utcDateString);
    if (isNaN(date.getTime())) return utcDateString;

    const { timeZone } = Intl.DateTimeFormat().resolvedOptions();
    const locale = getLocale();

    const formatOptions = {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      ...(options.includeTime && {
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
      }),
      hour12: is12HourFormat(),
      timeZone,
    };

    return new Intl.DateTimeFormat(locale, formatOptions).format(date);
  } catch (e) {
    return utcDateString;
  }
}

/**
 * Gets the current locale with fallback chain:
 * 1. Intl.DateTimeFormat().resolvedOptions().locale
 * 2. navigator.language
 * 3. i18n.language (if i18n is available)
 * 4. 'en' (default fallback)
 *
 * @param {Object} options - Configuration options
 * @param {boolean} [options.onlyLanguageCode=false] - If true, returns only the language code (e.g., 'en' instead of 'en-US')
 * @param {Object} [options.i18n] - Optional i18n instance to check for language
 * @returns {string} The detected locale
 */
export const getLocale = ({ onlyLanguageCode = false } = {}) => {
  let locale;

  try {
    // 1. Try Intl API first (most reliable)
    if (typeof Intl !== 'undefined' && Intl.DateTimeFormat) {
      locale = Intl.DateTimeFormat().resolvedOptions().locale;
    }

    // 2. Fallback to navigator.language
    if (!locale && typeof navigator !== 'undefined') {
      locale = navigator.language;
    }

    // 3. Fallback to i18n (always available)
    if (!locale && i18n?.language) {
      locale = i18n.language;
    }

    // 4. Final fallback
    if (!locale) {
      locale = 'en';
    }

    // Return only language code if requested
    return onlyLanguageCode ? locale.split('-')[0] : locale;
  } catch (error) {
    console.warn('Error detecting locale, falling back to "en"', error);
    return onlyLanguageCode ? 'en' : 'en-US';
  }
};
